import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle, Clock, AlertTriangle, Loader2, Plus, History,
  ChevronDown, ChevronUp, Wallet, Trash2, Receipt, CreditCard, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from './ui/select';
import { DatePicker } from './ui/date-picker';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const feeStatusConfig: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  paid: { label: 'Paid', color: 'text-lime-700 dark:text-lime-300', bg: 'bg-lime-100 dark:bg-lime-500/20', icon: CheckCircle },
  partial: { label: 'Partial', color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-100 dark:bg-amber-500/20', icon: Clock },
  due: { label: 'Due', color: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-100 dark:bg-rose-500/20', icon: AlertTriangle },
  overdue: { label: 'Overdue', color: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-100 dark:bg-rose-500/20', icon: AlertTriangle },
};

interface SharedFeesTabProps {
  studentId: string;
  invoices: any[];
  queryKey: string[];
  allowCreateInvoice?: boolean;
}

export default function SharedFeesTab({ studentId, invoices, queryKey, allowCreateInvoice }: SharedFeesTabProps) {
  const queryClient = useQueryClient();
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<number | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState<number | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', paymentMode: 'cash', notes: '', paymentDate: new Date().toISOString().split('T')[0] });
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ amount: '', paymentMode: 'cash', notes: '', paymentDate: '' });
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ amount: '', dueDate: '' });

  const expandedInvoiceIdRef = expandedInvoiceId;
  const [detailCache, setDetailCache] = useState<Record<number, any>>({});

  const fetchInvoiceDetail = async (invoiceId: number, force = false) => {
    if (!force && detailCache[invoiceId]) return detailCache[invoiceId];
    // Clear cache for this invoice to force fresh fetch
    setDetailCache(prev => { const next = { ...prev }; delete next[invoiceId]; return next; });
    const res = await api.get<any>(`/api/fees/invoices/${invoiceId}`);
    setDetailCache(prev => ({ ...prev, [invoiceId]: res.data }));
    return res.data;
  };

  const createInvoice = useMutation({
    mutationFn: async () => api.post('/api/fees/invoices', { studentId: Number(studentId), totalAmount: Number(invoiceForm.amount), dueDate: invoiceForm.dueDate }),
    onSuccess: () => {
      toast.success('Invoice created');
      setShowCreateForm(false);
      setInvoiceForm({ amount: '', dueDate: '' });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create invoice'),
  });

  const addPayment = useMutation({
    mutationFn: async ({ invoiceId, amount, paymentDate, paymentMode, notes }: any) => {
      return api.post('/api/fees/payments', { invoiceId, studentId: Number(studentId), amount, paymentDate, paymentMode, notes });
    },
    onSuccess: () => {
      toast.success('Payment recorded');
      setShowPaymentForm(null);
      setPaymentForm({ amount: '', paymentMode: 'cash', notes: '', paymentDate: new Date().toISOString().split('T')[0] });
      queryClient.invalidateQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
      // Force refresh the expanded invoice detail
      if (expandedInvoiceIdRef) {
        fetchInvoiceDetail(expandedInvoiceIdRef, true);
      }
    },
    onError: (err: any) => toast.error(err.message || 'Failed to record payment'),
  });

  const editPayment = useMutation({
    mutationFn: async ({ paymentId, data }: any) => api.patch(`/api/fees/payments/${paymentId}`, data),
    onSuccess: () => {
      toast.success('Payment updated');
      setEditingPaymentId(null);
      queryClient.invalidateQueries({ queryKey });
      queryClient.refetchQueries({ queryKey });
      if (expandedInvoiceIdRef) {
        fetchInvoiceDetail(expandedInvoiceIdRef, true);
      }
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update payment'),
  });

  const deleteInvoice = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/fees/invoices/${id}`),
    onSuccess: () => {
      toast.success('Invoice deleted');
      setConfirmDeleteId(null);
      setExpandedInvoiceId(null);
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && (key === 'dashboard-summary' || key === 'fees' || key === 'student-invoices');
      }});
    },
    onError: (err: any) => toast.error(err.message || 'Failed to delete invoice'),
  });

  const totals = useMemo(() => (invoices || []).reduce((acc: any, inv: any) => ({
    total: acc.total + (inv.total_amount || 0),
    paid: acc.paid + (inv.paid_amount || 0),
    due: acc.due + (inv.due_amount || 0),
  }), { total: 0, paid: 0, due: 0 }), [invoices]);

  const handleExpand = async (invoiceId: number) => {
    if (expandedInvoiceId === invoiceId) {
      setExpandedInvoiceId(null);
      return;
    }
    setExpandedInvoiceId(invoiceId);
    setShowPaymentForm(null);
    setEditingPaymentId(null);
    await fetchInvoiceDetail(invoiceId);
  };

  const expandedDetail = expandedInvoiceId ? detailCache[expandedInvoiceId] : null;
  const expandedPayments = expandedDetail?.payments || [];
  const expandedEditHistory = expandedDetail?.editHistory || [];

  if (invoices.length === 0 && !allowCreateInvoice) {
    return (
      <div className="text-center py-8">
        <Receipt className="w-10 h-10 sm:w-12 sm:h-12 text-light-text-muted mx-auto mb-3" />
        <p className="text-sm text-light-text-muted">No invoices yet</p>
      </div>
    );
  }

  if (invoices.length === 0 && allowCreateInvoice) {
    return (
      <div className="space-y-3">
        {!showCreateForm ? (
          <div className="text-center py-8">
            <Receipt className="w-10 h-10 sm:w-12 sm:h-12 text-light-text-muted mx-auto mb-3" />
            <p className="text-sm text-light-text-muted">No invoices yet</p>
            <Button size="sm" onClick={() => setShowCreateForm(true)} className="mt-3 gap-1.5">
              <Plus className="w-4 h-4" /> Create Invoice
            </Button>
          </div>
        ) : (
          <CreateInvoiceForm
            invoiceForm={invoiceForm}
            setInvoiceForm={setInvoiceForm}
            onSubmit={() => createInvoice.mutate()}
            onCancel={() => setShowCreateForm(false)}
            loading={createInvoice.isPending}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {allowCreateInvoice && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setShowCreateForm(true)} className="gap-1.5">
            <Plus className="w-4 h-4" /> Create Invoice
          </Button>
        </div>
      )}

      {showCreateForm && (
        <CreateInvoiceForm
          invoiceForm={invoiceForm}
          setInvoiceForm={setInvoiceForm}
          onSubmit={() => createInvoice.mutate()}
          onCancel={() => setShowCreateForm(false)}
          loading={createInvoice.isPending}
        />
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 text-center">
          <p className="text-[10px] text-amber-700 dark:text-amber-300">Total</p>
          <p className="text-sm font-bold text-amber-600 dark:text-amber-400">{formatCurrency(totals.total)}</p>
        </div>
        <div className="p-3 rounded-xl bg-lime-50 dark:bg-lime-500/10 text-center">
          <p className="text-[10px] text-lime-700 dark:text-lime-300">Paid</p>
          <p className="text-sm font-bold text-lime-600 dark:text-lime-400">{formatCurrency(totals.paid)}</p>
        </div>
        <div className={`p-3 rounded-xl text-center ${totals.due === 0 ? 'bg-lime-50 dark:bg-lime-500/10' : 'bg-rose-50 dark:bg-rose-500/10'}`}>
          <p className={`text-[10px] ${totals.due === 0 ? 'text-lime-700 dark:text-lime-300' : 'text-rose-700 dark:text-rose-300'}`}>Due</p>
          <p className={`text-sm font-bold ${totals.due === 0 ? 'text-lime-600 dark:text-lime-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {totals.due === 0 && <CheckCircle className="w-3.5 h-3.5 inline mr-0.5" />}
            {formatCurrency(totals.due)}
          </p>
        </div>
      </div>

      {/* Invoice List */}
      {invoices.map((invoice: any) => {
        const status = feeStatusConfig[invoice.status] || feeStatusConfig.due;
        const StatusIcon = status.icon;
        const isExpanded = expandedInvoiceId === invoice.id;
        const payments = isExpanded ? expandedPayments : [];
        const editHistory = isExpanded ? expandedEditHistory : [];

        return (
          <div key={invoice.id} className="rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
            <div
              onClick={() => handleExpand(invoice.id)}
              className="w-full p-3 flex items-center justify-between hover:bg-light-elevated/50 dark:hover:bg-dark-elevated/50 transition-colors text-left cursor-pointer"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${status.bg} ${status.color} shrink-0`}>
                    <StatusIcon className="w-2.5 h-2.5" />{status.label}
                  </span>
                  <span className="text-[10px] text-light-text-muted font-mono truncate">{invoice.invoice_no}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-light-text-muted">Total <strong className="text-light-text dark:text-dark-text">{formatCurrency(invoice.total_amount)}</strong></span>
                  <span className="text-light-text-muted">Paid <strong className="text-lime-600 dark:text-lime-400">{formatCurrency(invoice.paid_amount)}</strong></span>
                  <span className="text-light-text-muted">Due <strong className={invoice.due_amount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-lime-600 dark:text-lime-400'}>{formatCurrency(invoice.due_amount)}</strong></span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                {invoice.status !== 'paid' && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] gap-1 shrink-0"
                    onClick={(e) => { e.stopPropagation(); setShowPaymentForm(showPaymentForm === invoice.id ? null : invoice.id); }}>
                    <Plus className="w-3 h-3" /> Pay
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <MoreVertical className="w-3.5 h-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(invoice.id); }}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                      <span className="text-rose-500">Delete invoice</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-light-text-muted" /> : <ChevronDown className="w-4 h-4 text-light-text-muted" />}
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-light-border dark:border-dark-border px-3 pb-3">
                {showPaymentForm === invoice.id && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-semibold text-light-text dark:text-dark-text">Record Payment — {invoice.invoice_no}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" placeholder="Amount" max={invoice.due_amount} value={paymentForm.amount}
                        onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                      <input type="date" value={paymentForm.paymentDate}
                        onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                        className="flex h-9 w-full rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-3 py-1 text-xs text-light-text dark:text-dark-text shadow-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={paymentForm.paymentMode} onValueChange={(v) => setPaymentForm({ ...paymentForm, paymentMode: v })}>
                        <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Notes" value={paymentForm.notes}
                        onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" disabled={addPayment.isPending || !paymentForm.amount || Number(paymentForm.amount) <= 0}
                        onClick={() => addPayment.mutate({ invoiceId: invoice.id, amount: Number(paymentForm.amount), paymentDate: paymentForm.paymentDate, paymentMode: paymentForm.paymentMode, notes: paymentForm.notes })}>
                        {addPayment.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wallet className="w-3 h-3" />} Record Payment
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setShowPaymentForm(null)}>Cancel</Button>
                    </div>
                  </div>
                )}

                {payments.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <History className="w-3.5 h-3.5 text-light-text-muted" />
                      <span className="text-xs font-semibold text-light-text dark:text-dark-text">Payment History</span>
                    </div>
                    <div className="space-y-1.5">
                      {payments.map((payment: any) => (
                        <div key={payment.id} className="p-2.5 rounded-lg bg-light-elevated/30 dark:bg-dark-elevated/30 border border-light-border dark:border-dark-border">
                          {editingPaymentId === payment.id ? (
                            <div className="grid grid-cols-2 gap-2">
                              <Input type="number" placeholder="Amount" value={editForm.amount}
                                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} />
                              <input type="date" value={editForm.paymentDate}
                                onChange={(e) => setEditForm({ ...editForm, paymentDate: e.target.value })}
                                className="flex h-9 w-full rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-3 py-1 text-xs text-light-text dark:text-dark-text shadow-sm" />
                              <Select value={editForm.paymentMode} onValueChange={(v) => setEditForm({ ...editForm, paymentMode: v })}>
                                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cash">Cash</SelectItem>
                                  <SelectItem value="upi">UPI</SelectItem>
                                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                  <SelectItem value="cheque">Cheque</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-end gap-1">
                                <Button size="sm" className="h-7 text-[10px]" disabled={editPayment.isPending}
                                  onClick={() => editPayment.mutate({ paymentId: payment.id, data: { amount: Number(editForm.amount), paymentDate: editForm.paymentDate, paymentMode: editForm.paymentMode, notes: editForm.notes } })}>
                                  {editPayment.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => setEditingPaymentId(null)}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-xs flex-wrap">
                                  <strong className="text-lime-600 dark:text-lime-400">{formatCurrency(payment.amount)}</strong>
                                  <span className="text-light-text-muted">|</span>
                                  <span className="text-light-text dark:text-dark-text">{formatDate(payment.payment_date)}</span>
                                  <span className="text-light-text-muted">|</span>
                                  <span className="text-[10px] capitalize bg-light-elevated dark:bg-dark-elevated px-1.5 py-0.5 rounded">{payment.payment_mode}</span>
                                  {payment.notes && <><span className="text-light-text-muted">|</span><span className="text-light-text-muted">{payment.notes}</span></>}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {payment.edited_at && (
                                    <span className="text-[9px] text-amber-500 flex items-center gap-0.5" title={`Edited ${formatDate(payment.edited_at)}`}>
                                      <History className="w-2.5 h-2.5" />Edited
                                    </span>
                                  )}
                                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[9px]"
                                    onClick={() => { setEditingPaymentId(payment.id); setEditForm({ amount: String(payment.amount), paymentMode: payment.payment_mode, notes: payment.notes || '', paymentDate: payment.payment_date }); }}>
                                    Edit
                                  </Button>
                                </div>
                              </div>
                              <div className="text-[9px] text-light-text-muted flex items-center gap-2 mt-0.5">
                                <span className="font-mono">Receipt: {payment.receipt_no}</span>
                                {payment.collected_by_name && <span>Collected by: {payment.collected_by_name}</span>}
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    {editHistory.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[10px] text-light-text-muted cursor-pointer hover:text-light-text dark:hover:text-dark-text">
                          {editHistory.length} edit{editHistory.length !== 1 ? 's' : ''} made
                        </summary>
                        <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                          {editHistory.map((entry: any, i: number) => (
                            <p key={i} className="text-[9px] text-light-text-muted">
                              <span className="font-medium">{entry.edited_by_name || 'Unknown'}</span>
                              {' '}changed <span className="font-medium">{entry.field_name}</span>
                              {' '}from "{entry.old_value}" to "{entry.new_value}"
                              {' '}at {formatDate(entry.created_at)}
                            </p>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {payments.length === 0 && showPaymentForm !== invoice.id && (
                  <div className="mt-3 text-center py-3">
                    <p className="text-xs text-light-text-muted">No payments recorded yet</p>
                    <Button variant="ghost" size="sm" className="mt-1 h-7 text-xs gap-1" onClick={() => setShowPaymentForm(invoice.id)}>
                      <Plus className="w-3 h-3" /> Add First Payment
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <AlertDialog open={!!confirmDeleteId} onOpenChange={() => setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete invoice?</AlertDialogTitle>
            <AlertDialogDescription>This invoice will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => confirmDeleteId && deleteInvoice.mutate(confirmDeleteId)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateInvoiceForm({ invoiceForm, setInvoiceForm, onSubmit, onCancel, loading }: any) {
  return (
    <div className="rounded-xl border border-light-border dark:border-dark-border bg-white dark:bg-dark-surface p-3 space-y-2">
      <p className="text-xs font-semibold text-light-text dark:text-dark-text">Create Invoice</p>
      <div className="grid grid-cols-2 gap-2">
        <Input type="number" placeholder="Total amount" value={invoiceForm.amount}
          onChange={(e: any) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })} />
        <DatePicker value={invoiceForm.dueDate} onChange={(v: string) => setInvoiceForm({ ...invoiceForm, dueDate: v })} placeholder="Due date" />
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onSubmit} disabled={loading || !invoiceForm.amount}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Create
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
